import React from 'react';
import { ArrowRight, Play } from 'lucide-react';
import { heroContent, statsData } from '../data/mock';
import { Button } from './ui/button';

const Hero = () => {
  return (
    <section className="relative min-h-screen overflow-hidden">
      {/* Animated gradient background - Stripe style */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-400 via-pink-500 via-purple-500 to-blue-600 animate-gradient" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLW9wYWNpdHk9IjAuMDUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-30" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[calc(100vh-200px)]">
          {/* Left content */}
          <div>
            {/* Badge */}
            <a
              href="#community"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium hover:bg-white/20 transition-colors mb-8 border border-white/20"
            >
              <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs">New</span>
              {heroContent.badge}
              <ArrowRight className="w-4 h-4" />
            </a>

            {/* Headline */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
              <span className="block">Spiritual</span>
              <span className="block">infrastructure to</span>
              <span className="block bg-gradient-to-r from-white via-white/90 to-white/70 bg-clip-text text-transparent">
                strengthen your
              </span>
              <span className="block bg-gradient-to-r from-purple-200 to-pink-200 bg-clip-text text-transparent">
                faith
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg text-white/80 max-w-lg mb-8 leading-relaxed">
              {heroContent.subheadline}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <Button
                className="bg-white text-slate-900 hover:bg-white/90 px-6 py-6 text-base font-semibold rounded-full shadow-lg hover:shadow-xl transition-all group"
              >
                {heroContent.primaryCta}
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 px-6 py-6 text-base font-semibold rounded-full backdrop-blur-sm"
              >
                <Play className="w-4 h-4 mr-2" />
                {heroContent.secondaryCta}
              </Button>
            </div>
          </div>

          {/* Right content - Feature cards */}
          <div className="relative hidden lg:block">
            <div className="relative">
              {/* Main card */}
              <div className="bg-white rounded-3xl shadow-2xl p-6 transform rotate-1 hover:rotate-0 transition-transform duration-500">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold">
                    3
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">3strand Cord</h3>
                    <p className="text-sm text-slate-500">Your spiritual journey</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="p-4 bg-slate-50 rounded-2xl">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-700">Spiritual Growth</span>
                      <span className="text-xs text-emerald-600 font-medium">+32%</span>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div className="h-full w-3/4 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full" />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl">
                      <span className="text-2xl font-bold text-emerald-700">12</span>
                      <p className="text-sm text-emerald-600">Topics Explored</p>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl">
                      <span className="text-2xl font-bold text-purple-700">7</span>
                      <p className="text-sm text-purple-600">Pillars of Wisdom</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating cards */}
              <div className="absolute -top-8 -right-8 bg-white rounded-2xl shadow-xl p-4 transform hover:-translate-y-1 transition-transform">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center">
                    <span className="text-white text-xl">🙏</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">Prayer</p>
                    <p className="text-xs text-slate-500">Daily communion</p>
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-4 -left-8 bg-white rounded-2xl shadow-xl p-4 transform hover:translate-y-1 transition-transform">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center">
                    <span className="text-white text-xl">✨</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">Meditation</p>
                    <p className="text-xs text-slate-500">Be still & know</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-16 pt-8 border-t border-white/10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {statsData.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-white/70">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent" />
    </section>
  );
};

export default Hero;
